import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin, useMe } from '../../api/session';
import type { ProjectSummary, UserDetail } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/forms';
import { ConfirmDialog } from '../../ui/Modal';
import { Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** IDN-03/04: role, active state, memberships, sessions; last admin is protected server-side. */
export function UserDetailPage() {
  const { userId } = useParams();
  const admin = useIsAdmin();
  const me = useMe();
  const q = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api.get<UserDetail>(`/admin/users/${userId}`),
    enabled: admin,
  });
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectSummary[]>('/projects'),
    enabled: admin,
  });
  usePageTitle(q.data ? `${q.data.displayName} · Account administration` : null);
  useCrumbs([{ label: 'Users', to: '/admin/users' }, { label: q.data?.displayName ?? '' }]);
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState<'deactivate' | 'reactivate' | 'role' | 'sessions' | null>(null);
  const [memberships, setMemberships] = useState<string[]>([]);
  useEffect(() => {
    if (q.data) {
      setRole(q.data.role);
      setMemberships(q.data.projects.map((p) => p.id));
    }
  }, [q.data]);
  const invalidate = [['user', userId], ['users']];
  const update = useAction(
    (body: object) =>
      api.patch(`/admin/users/${userId}`, {
        ...body,
        expectedVersion: q.data!.version,
        reason: reason || undefined,
      }),
    { success: 'Account updated', invalidate, onSuccess: () => setConfirm(null) },
  );
  const saveMemberships = useAction(
    () => api.put(`/admin/users/${userId}/memberships`, { projectIds: memberships }),
    { success: 'Memberships saved', invalidate },
  );
  const revoke = useAction(() => api.post(`/admin/users/${userId}/revoke-sessions`), {
    success: 'Sessions revoked',
    invalidate,
    onSuccess: () => setConfirm(null),
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <QueryView query={q}>
        {(u) => (
          <>
            <PageHeader
              title={u.displayName}
              subtitle={u.email}
              actions={u.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
            />
            <div className="split">
              <div className="stack">
                <Card title="Role">
                  <div className="row nowrap" style={{ alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <Field label="Application role" hint="Identity-provider groups never change this.">
                        {(p) => (
                          <Select
                            {...p}
                            value={role}
                            onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
                          >
                            <option value="user">Member</option>
                            <option value="admin">Admin</option>
                          </Select>
                        )}
                      </Field>
                    </div>
                    <Button variant="secondary" disabled={role === u.role} onClick={() => setConfirm('role')}>
                      Change role
                    </Button>
                  </div>
                </Card>
                <Card
                  title="Project memberships"
                  subtitle="Removal ends access on the next request; contributions stay attributed."
                >
                  <div className="stack sm">
                    {(projects.data ?? []).map((p) => (
                      <Checkbox
                        key={p.id}
                        label={
                          <>
                            <span className="mono muted">{p.key}</span> {p.name}
                            {p.archived ? ' (archived)' : ''}
                          </>
                        }
                        checked={memberships.includes(p.id)}
                        onChange={(on) =>
                          setMemberships(on ? [...memberships, p.id] : memberships.filter((x) => x !== p.id))
                        }
                      />
                    ))}
                  </div>
                  <ErrorAlert error={saveMemberships.error} />
                  <div>
                    <Button
                      onClick={() => saveMemberships.mutate(undefined)}
                      loading={saveMemberships.isPending}
                    >
                      Save memberships
                    </Button>
                  </div>
                </Card>
                <Card title="Lifecycle">
                  <div className="row">
                    {u.active ? (
                      <Button
                        variant="danger-outline"
                        onClick={() => setConfirm('deactivate')}
                        disabled={u.id === me.user.id}
                      >
                        Deactivate account
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={() => setConfirm('reactivate')}>
                        Reactivate account
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => setConfirm('sessions')}
                      disabled={u.activeSessions === 0}
                    >
                      Revoke sessions ({u.activeSessions})
                    </Button>
                  </div>
                  {u.id === me.user.id && (
                    <span className="small muted">You cannot deactivate your own account.</span>
                  )}
                </Card>
              </div>
              <Card title="Identity" tight>
                <KeyValue
                  items={[
                    [
                      'Issuer',
                      <span key="i" className="mono small">
                        {u.issuer}
                      </span>,
                    ],
                    [
                      'Subject',
                      <span key="s" className="mono small">
                        {u.subject}
                      </span>,
                    ],
                    ['Joined', <Timestamp key="j" value={u.createdAt} />],
                    ['Last sign-in', <Timestamp key="l" value={u.lastSignInAt} />],
                    ['Deactivated', <Timestamp key="d" value={u.deactivatedAt} />],
                  ]}
                />
                <span className="small muted">
                  Issuer and subject are the stable identity key. Email is contact information only.
                </span>
              </Card>
            </div>
            {confirm && confirm !== 'sessions' && (
              <ConfirmDialog
                title={
                  confirm === 'role'
                    ? `Change ${u.displayName} to ${role === 'admin' ? 'Admin' : 'Member'}?`
                    : confirm === 'deactivate'
                      ? `Deactivate ${u.displayName}?`
                      : `Reactivate ${u.displayName}?`
                }
                confirmLabel={confirm === 'deactivate' ? 'Yes, deactivate' : 'Confirm'}
                variant={confirm === 'deactivate' ? 'danger' : 'primary'}
                onClose={() => setConfirm(null)}
                onConfirm={() =>
                  update.mutate(confirm === 'role' ? { role } : { active: confirm === 'reactivate' })
                }
                pending={update.isPending}
                error={update.error}
              >
                <p>
                  {confirm === 'deactivate'
                    ? 'All sessions end immediately. Tickets, comments, and approvals stay attributed. The last active admin cannot be deactivated.'
                    : confirm === 'role'
                      ? 'Admins can access every project and approve work and releases.'
                      : 'The person can sign in again.'}
                </p>
                <Field label="Reason" optional>
                  {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
                </Field>
              </ConfirmDialog>
            )}
            {confirm === 'sessions' && (
              <ConfirmDialog
                title="Revoke all sessions?"
                confirmLabel="Revoke sessions"
                variant="danger"
                onClose={() => setConfirm(null)}
                onConfirm={() => revoke.mutate(undefined)}
                pending={revoke.isPending}
                error={revoke.error}
              >
                <p>The person is signed out everywhere and must sign in again.</p>
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}
